<template>
    <QLayout view="lHh LpR lFr" class="ProcessLayout">
        <Sidebar
            :modelValue="tab"
            :tabs="tabs"
            @update:modelValue="handleChangeTab"
        />

        <QDrawer
            v-if="hasContextDrawer"
            v-model="rightDrawerOpen"
            side="right"
            :overlay="isMobile"
            :width="320"
            class="ContextDrawer q-px-sm"
        >
            <template v-if="hasDisplayDrawerTab">
                <div class="ContextDrawerHeader">
                    <q-tabs
                        v-model="contextDrawerTab"
                        dense
                        align="justify"
                        noCaps
                        inlineLabel
                        activeColor="primary"
                        indicatorColor="primary"
                        class="Tabs ContextDrawerTabs text-grey-8 full-width"
                    >
                        <q-tab
                            name="MAIN"
                            :label="drawerPrimaryTitle"
                            :icon="drawerIcon"
                        />
                        <q-tab
                            name="DISPLAY"
                            :label="$t('process.display')"
                            icon="tune"
                        />
                    </q-tabs>
                </div>

                <q-separator color="primary" />

                <q-tab-panels
                    v-model="contextDrawerTab"
                    animated
                    keepAlive
                >
                    <q-tab-panel name="MAIN" class="q-pa-none">
                        <div class="q-pa-md">
                            <component
                                :is="contextDrawerPanel.component"
                                v-bind="contextDrawerPanel.props"
                                ref="contextPanel"
                                :key="routeName"
                                v-on="contextDrawerPanel.on"
                            />
                        </div>
                    </q-tab-panel>

                    <q-tab-panel name="DISPLAY" class="q-pa-none">
                        <div class="q-pa-md">
                            <ProcessDisplayDrawerPanel
                                :key="`${routeName}-display`"
                                :disableElements="disableComponents || drawerBusy"
                                :canSaveBpmn="drawerDisplay.canSaveBpmn"
                                :percentageMode="drawerDisplay.percentageMode"
                                :directionValue="drawerDisplay.directionValue"
                                :statsValue="drawerDisplay.statsValue"
                                :heatmapMode="drawerDisplay.heatmapMode"
                                :showHeatmapOption="routeName === 'ProcessVariants'"
                                :frequencyIndicatorState="drawerDisplay.frequencyIndicatorState"
                                @on-change-percentage="handleDrawerChangePercentage"
                                @on-change-direction="handleDrawerChangeDirection"
                                @on-change-stats="handleDrawerChangeStats"
                                @on-change-heatmap="handleDrawerChangeHeatmap"
                                @on-save-bpmn="handleDrawerSaveBpmn"
                                @on-increase-frequency-indicator="handleDrawerIncreaseFrequencyIndicator"
                                @on-decrease-frequency-indicator="handleDrawerDecreaseFrequencyIndicator"
                            />
                        </div>
                    </q-tab-panel>
                </q-tab-panels>
            </template>

            <template v-else>
                <div class="ContextDrawerHeader">
                    <div class="flex items-center no-wrap">
                        <q-icon
                            :name="drawerIcon"
                            color="primary"
                            size="20px"
                            class="q-mr-sm"
                        />
                        <div class="ContextDrawerTitle text-primary">
                            {{ drawerPrimaryTitle }}
                        </div>
                    </div>
                </div>

                <q-separator color="primary" />

                <div class="q-pa-md">
                    <component
                        :is="contextDrawerPanel.component"
                        v-bind="contextDrawerPanel.props"
                        ref="contextPanel"
                        :key="routeName"
                        v-on="contextDrawerPanel.on"
                    />
                </div>
            </template>
        </QDrawer>

        <QHeader elevated class="Header">
            <AdminHeader>
                <template v-if="hasContextDrawer" #actions>
                    <div class="flex row-stretch-center">
                        <QBtn
                            flat
                            round
                            dense
                            color="primary"
                            icon="view_sidebar"
                            :aria-label="$t('general.filters')"
                            @click="rightDrawerOpen = !rightDrawerOpen"
                        />
                    </div>
                </template>
            </AdminHeader>
        </QHeader>

        <QPageContainer class="Container">
            <QPage
                class="Screen flex-1"
                :class="{ isDesktop: !isMobile }"
            >
                <RouterView v-slot="{ Component, route }">
                    <Transition
                        name="main-router"
                        mode="out-in"
                    >
                        <component
                            :is="Component"
                            :key="route.fullPath"
                            ref="activeView"
                            :processId="processId"
                            :infoTotal="infoTotal"
                            :infoFiltered="infoFiltered"
                            :attributes="columnAttributes"
                            :categoricalAttributes="categoricalAttributes"
                            :numericAttributes="numericAttributes"
                            @display-state-changed="handleActiveViewDisplayStateChanged"
                            @frequency-indicator-state-changed="handleActiveFrequencyIndicatorStateChanged"
                            @dashboards-drawer-state-changed="handleDashboardsDrawerStateChanged"
                        />
                    </Transition>
                </RouterView>
            </QPage>
        </QPageContainer>

        <QFooter
            v-if="hasFooter"
            class="Footer"
        >
            <FiltersSidebar
                ref="filtersSidebar"
                :processId="processId"
                :infoTotal="infoTotal"
                :filteredInfo="infoFiltered"
                @on-manage-filter="handleOpenFiltersModal"
                @on-manage-filter-sets="handleOpenFilterSetsModal"
                @on-delete-click="handleDeleteFilter"
                @on-apply="handleApplyFilters"
                @on-replace-filters="handleApplyFilters"
                @on-reset="handleResetFilters"
                @on-manage-create-filter-sets="handleOpenCreateFilterSetsModal"
                @on-repeat-calls="handleReloadTabData"
            />
            <q-inner-loading :showing="disableComponents" class="q-pa-md" />
        </QFooter>
        <FilterModal
            v-if="processId"
            ref="FilterModal"
            :info="infoTotal"
            :processId="processId"
            :attributes="columnAttributes"
            :isOpen="openFilterModal"
            :categoricalAttributes="categoricalAttributes"
            @on-save-filter="handleAddFilter"
            @close="handleCloseFiltersModal"
        />

        <RenameModel
            ref="renameVariantModal"
            :isVariant="true"
            @save="handleRenameVariant"
        />
        <RenameModel
            ref="renameLoopModal"
            :isVariant="false"
            @save="handleRenameLoop"
        />
        <FilterSetsModal
            v-if="processId"
            ref="FilterSetsModal"
            class="FilterSetsModal"
            :datasetId="processId"
            :activeFilters="visualizationFilters"
            :isOpen="openFilterSetsModal"
            @add-to-active="handleAddToActiveFilters"
            @replace-filters="handleReplaceActiveFilters"
            @removed-filter-sets="handleRemoveFilterSets"
            @repeat-calls="handleReloadTabData"
            @close="handleCloseFiltersetSetsModal"
        />
        <CreateFilterSetsModal
            v-if="processId"
            ref="CreateFilterSetsModal"
            :datasetId="processId"
            :activeFilters="visualizationFilters"
            :isOpen="openCreateFilterSetsModal"
            @repeat-calls="handleReloadTabData"
            @close="handleCloseCreateFilterSetsModal"
        />
    </QLayout>
</template>
<script>
import { filtersStorageMixin, mediaQueryMixin } from '@/mixins'
import { Api, apiRequest, notifyError } from '@/api'
import { AdminHeader, Sidebar } from '@/components'
import {
    canonicalizeFiltersToAdd,
    canonicalizeFilterSetAsFilter,
    canonicalizeFiltersFromFilterSet,
} from '@/utils/filters'
import {
    ContextFilterBar as FiltersSidebar,
    FilterModal,
    FilterSetsModal,
    CreateFilterSetsModal,
} from './Filters'

import RenameModel from './Visualization/components/RenameModel.vue'

import {
    ProcessVariantsDrawerPanel,
    ProcessLoopsDrawerPanel,
    ProcessDisplayDrawerPanel,
    ProcessConformanceDrawerPanel,
    ProcessTracesDrawerPanel,
    ProcessDashboardsDrawerPanel,
    getProcessContextDrawerPanel,
} from './contextDrawerPanels'

export default {
    name: 'ProcessLayout',
    components: {
        AdminHeader,
        Sidebar,
        FiltersSidebar,
        FilterModal,
        FilterSetsModal,
        CreateFilterSetsModal,
        RenameModel,
        ProcessVariantsDrawerPanel,
        ProcessLoopsDrawerPanel,
        ProcessDisplayDrawerPanel,
        ProcessConformanceDrawerPanel,
        ProcessTracesDrawerPanel,
        ProcessDashboardsDrawerPanel,
    },
    mixins: [filtersStorageMixin, mediaQueryMixin],
    data() {
        return {
            tab: 'ProcessVariants',
            rightDrawerOpen: true,
            contextDrawerTab: 'MAIN',
            parentCoords: {},
            categoricalAttributes: undefined,
            numericAttributes: undefined,
            columnAttributes: undefined,
            processId: this.$route.params.processId || undefined,
            infoTotal: undefined,
            infoFiltered: undefined,
            filteredTraces: 0,
            attributes: undefined,
            infoLoading: true,
            filteredInfoLoading: true,
            attributesLoading: true,
            openFilterModal: false,
            openFilterSetsModal: false,
            openCreateFilterSetsModal: false,
            miniState: true,
            drawerBusy: false,
            variantRenamedId: undefined,
            loopRenamedId: undefined,
            drawerModel: { variant: 1, values: 'both' },
            drawerDisplay: {
                percentageMode: 'TOTAL',
                directionValue: 'LR',
                statsValue: 'averages',
                heatmapMode: 'OFF',
                canSaveBpmn: false,
                frequencyIndicatorState: {
                    visible: false,
                    value: 100,
                    minValue: 0,
                    canIncrease: false,
                    canDecrease: false
                },
            }, 

            conformanceModelId: undefined,
            conformanceModelsCount: 0,

            tracesDrawerSearch: '',

            dashboardsDrawer: {
                editMode: false,
                graphObj: null,
                sideTab: 'graphsList',
                layoutData: [],
                dashboardFilters: [],
                allFilters: [],
                attributes: [],
                categoricalAttributes: [],
                numericAttributes: [],
            },

            lastRouteName: undefined,

            addFilterListener: null,
        }
    },
    computed: {
        disableComponents() {
            return this.infoLoading || this.filteredInfoLoading || this.attributesLoading
        },
        routeName() {
            return this.$route?.name
        },
        hasFooter() {
            return !!this.$route?.meta?.footer
        },
        tabs() {
            return [
                { id: 'ProcessVariants', icon: 'account_tree', label: this.$t('visualization.tabs.process') },
                { id: 'ProcessRework', icon: '360', label: this.$t('visualization.tabs.loops') },
                { id: 'ProcessConformance', icon: 'fact_check', label: this.$t('visualization.tabs.conformance') },
                { id: 'ProcessTraces', icon: 'list', label: this.$t('visualization.tabs.traces') },
                { id: 'ProcessDashboards', icon: 'dashboard', label: this.$t('process.dashboards') },
                { id: 'ProcessBooster', icon: 'batch_prediction', label: this.$t('process.insightsAndAlerts') },
                { id: 'ProcessTools', icon: 'build', label: this.$t('process.analysisTools') },
                { id: 'ProcessSettings', icon: 'settings', label: this.$t('process.settings') },
            ]
        },
        navIdFromRoute() {
            const toolsChildren = ['ProcessAttributes', 'ProcessMetrics', 'ProcessComparisons']
            const boosterChildren = ['ProcessOverview', 'ProcessPrediction', 'ProcessSimulation', 'ProcessAlerts']

            if (toolsChildren.includes(this.routeName)) return 'ProcessTools'
            if (this.routeName === 'ProcessTools') return 'ProcessTools'

            if (boosterChildren.includes(this.routeName)) return 'ProcessBooster'
            if (this.routeName === 'ProcessBooster') return 'ProcessBooster'

            return this.routeName
        },
        contextDrawerPanel() {
            return getProcessContextDrawerPanel(this)
        },
        hasContextDrawer() {
            return !!(this.$route?.meta?.contextDrawer && this.contextDrawerPanel?.component)
        },
        drawerPrimaryTitle() {
            return this.contextDrawerPanel?.title || ''
        },
        drawerIcon() {
            return this.contextDrawerPanel?.icon || 'tune'
        },
        hasDisplayDrawerTab() {
            return ['ProcessVariants', 'ProcessRework'].includes(this.routeName)
        },
    },
    watch: {
        contextDrawerTab: {
            handler(val) {
                if (val === 'DISPLAY') {
                    this.syncDrawerDisplayFromView()
                }
            },
            immediate: true,
        },
        '$route.fullPath': {
            handler() {
                const prev = this.lastRouteName
                this.tab = this.navIdFromRoute
                this.contextDrawerTab = 'MAIN'

                // Default drawer behavior: keep it closed for Dashboards to maximize space.
                // When leaving Dashboards, restore default open state.
                if (this.routeName === 'ProcessDashboards') {
                    this.rightDrawerOpen = false
                } else if (prev === 'ProcessDashboards') {
                    this.rightDrawerOpen = true
                }

                if (this.routeName !== 'ProcessVariants' && this.drawerDisplay.heatmapMode !== 'OFF') {
                    this.drawerDisplay.heatmapMode = 'OFF'
                    this.callActiveView('setHeatmapModeValue', 'OFF')
                }

                this.lastRouteName = this.routeName

                if (this.routeName === 'ProcessConformance') {
                    this.restoreConformanceSelectionFromStorage()
                }
            },
            immediate: true,
        },
        '$route.params.processId': {
            handler: 'onProcessIdChanged',
            immediate: true,
        },
    },
    created() {
        this.tab = this.navIdFromRoute
    },
    mounted() {
        this.listenAddFilter()
    },
    beforeUnmount() {
        if (this.addFilterListener) {
            this.$eventBus.off('addFilter', this.addFilterListener)
        }
    },
    methods: {
        conformanceStorageKey() {
            const { projectId } = this.$route?.params || {}
            const { processId } = this
            return `process.conformance.selectedModel.${projectId}.${processId}`
        },
        restoreConformanceSelectionFromStorage() {
            try {
                const key = this.conformanceStorageKey()
                const stored = localStorage.getItem(key)
                if (!stored) return
                this.conformanceModelId = stored
                this.callActiveView('setConformanceModelId', stored)
            } catch (e) {
                // intentionally empty
            }
        },
        persistConformanceSelectionToStorage(modelId) {
            try {
                const key = this.conformanceStorageKey()
                if (!modelId) {
                    localStorage.removeItem(key)
                    return
                }
                localStorage.setItem(key, `${modelId}`)
            } catch (e) {
                // intentionally empty
            }
        },

        handleConformanceModelSelected({ modelId, modelName } = {}) {
            this.conformanceModelId = modelId
            this.persistConformanceSelectionToStorage(modelId)
            this.callActiveView('setConformanceModelId', modelId)
            // Optional: keep model name map in the view.
            if (modelName) {
                this.callActiveView('setConformanceModelName', { modelId, modelName })
            }
        },
        handleConformanceModelsLoaded({ count, models } = {}) {
            this.conformanceModelsCount = Number(count || 0)
            this.callActiveView('setConformanceModelsInfo', { count, models })
        },
        handleConformanceModelPreview({ modelId, modelName } = {}) {
            this.callActiveView('openConformanceModelPreview', { modelId, modelName })
        },
        handleActiveViewDisplayStateChanged(state) {
            if (!state || typeof state !== 'object') return

            const next = {
                percentageMode: state.percentageMode,
                directionValue: state.directionValue,
                statsValue: state.statsValue,
                heatmapMode: state.heatmapMode,
                canSaveBpmn: state.canSaveBpmn,
            }

            Object.keys(next).forEach((key) => {
                if (next[key] !== undefined) {
                    this.drawerDisplay[key] = next[key]
                }
            })
        },
        handleActiveFrequencyIndicatorStateChanged(state) {
            if (!state || typeof state !== 'object') return

            this.drawerDisplay.frequencyIndicatorState = { ...state }
        },

        handleDashboardsDrawerStateChanged(state) {
            if (!state || typeof state !== 'object') return

            const next = {
                editMode: state.editMode,
                graphObj: state.graphObj,
                sideTab: state.sideTab,
                layoutData: state.layoutData,
                dashboardFilters: state.dashboardFilters,
                allFilters: state.allFilters,
                attributes: state.attributes,
                categoricalAttributes: state.categoricalAttributes,
                numericAttributes: state.numericAttributes,
            }

            Object.keys(next).forEach((key) => {
                if (next[key] !== undefined) {
                    this.dashboardsDrawer[key] = next[key]
                }
            })
        },
        syncDrawerDisplayFromView() {
            const view = this.$refs.activeView
            const state = view?.getDisplayState?.()
            if (!state || typeof state !== 'object') return

            const next = {
                percentageMode: state.percentageMode,
                directionValue: state.directionValue,
                statsValue: state.statsValue,
                heatmapMode: state.heatmapMode,
                canSaveBpmn: state.canSaveBpmn,
            }

            Object.keys(next).forEach((key) => {
                if (next[key] !== undefined) {
                    this.drawerDisplay[key] = next[key]
                }
            })

            const frequencyState = view?.getFrequencyIndicatorState?.()
            if (frequencyState && typeof frequencyState === 'object') {
                this.drawerDisplay.frequencyIndicatorState = { ...frequencyState }
            }
        },
        callActiveView(methodName, ...args) {
            // During route transitions, the new `activeView` ref may not be ready yet.
            // Retry briefly so early drawer emissions (e.g. onLoadChart) aren't lost.
            const MAX_ATTEMPTS = 10
            const RETRY_DELAY_MS = 40

            const attempt = (remaining) => {
                const view = this.$refs.activeView
                const fn = view?.[methodName]
                if (typeof fn === 'function') {
                    fn.apply(view, args)
                    return
                }
                if (remaining <= 0) return
                setTimeout(() => {
                    this.$nextTick(() => attempt(remaining - 1))
                }, RETRY_DELAY_MS)
            }

            attempt(MAX_ATTEMPTS)
        },
        handleChangeTab(value) {
            if (!value) return
            if (value === this.tab) return

            if (value === 'ProcessTools') {
                this.$router.push({ name: 'ProcessAttributes', params: this.$route.params, query: { ...this.$route.query } }).catch(() => {})
                return
            }

            if (value === 'ProcessBooster') {
                this.$router.push({ name: 'ProcessOverview', params: this.$route.params, query: { ...this.$route.query } }).catch(() => {})
                return
            }

            this.$router.push({ name: value, params: this.$route.params, query: { ...this.$route.query } }).catch(() => {})
        },
        async onProcessIdChanged(newProcessId, oldProcessId) {
            if (!newProcessId) return

            const isInitial = oldProcessId === undefined || oldProcessId === null
            const storedProcessId = this.visualizationFiltersProcessId
            const hasChanged = `${newProcessId}` !== `${oldProcessId || ''}`

            const hasStoredProcessId = storedProcessId !== undefined && storedProcessId !== null && `${storedProcessId}` !== ''
            const hasStoredFilters = Array.isArray(this.visualizationFilters) && this.visualizationFilters.length > 0

            // Initial load:
            // - If we have filters but no stored processId (migration), keep filters and bind them to this processId.
            // - Otherwise, reset only when the stored processId differs.
            let shouldReset = hasChanged
            if (isInitial) {
                if (hasStoredProcessId) {
                    shouldReset = `${storedProcessId}` !== `${newProcessId}`
                } else {
                    shouldReset = !hasStoredFilters
                }
            }
            this.processId = newProcessId

            if (isInitial && !hasStoredProcessId && hasStoredFilters) {
                this.setVisualizationFiltersProcessId(newProcessId)
            }

            if (shouldReset) {
                this.resetStorage(newProcessId)
                this.setGeneralOperator('AND')
                this.openFilterModal = false
                this.openFilterSetsModal = false
                this.openCreateFilterSetsModal = false
            }

            this.getModelInfoFiltered()
            this.getModelInfo()
            this.getAttributes()
        },
        async getAttributes() {
            this.attributesLoading = true
            const { processId } = this.$route.params
            await this.getColumnAttributes(processId)
            await this.getCategoricalAttributes()
            await this.getNumericAttributes(processId)
            this.attributesLoading = false
        },
        async getColumnAttributes(processId) {
            apiRequest(Api().datasets.getConfigurations({ datasetId: processId }))
                .then(({ data }) => {
                    const [datasetConfig] = data
                    this.columnAttributes = datasetConfig.columns
                })
                .catch(notifyError)
                .finally()
        },
        async getCategoricalAttributes() {
            const { processId } = this
            const { filters, filterSets } = this.getActiveFiltersApiParts()
            const params = { filters, filterSets }
            apiRequest(Api().visualizations.attributes({ processId, params }))
                .then((res) => {
                    this.categoricalAttributes = res
                })
                .catch(notifyError)
                .finally()
        },
        async getNumericAttributes(processId) {
            const { filters, filterSets, operator } = this.getActiveFiltersApiParts()
            const params = { filters, filterSets, operator }
            return apiRequest(Api().visualizations.numericalAttributes({ processId, params }))
                .then((res) => {
                    this.numericAttributes = res
                })
                .catch(notifyError)
        },
        /// /////////////////////////////////////////
        /// /////////////// SIDEBAR /////////////////
        /// /////////////////////////////////////////

        async getModelInfo() {
            this.infoLoading = true
            const { processId } = this
            const params = { filters: [] }
            apiRequest(Api().visualizations.modelInfo({ processId, params }))
                .then((data) => {
                    this.infoTotal = data
                })
                .catch(notifyError)
                .finally(() => {
                    this.infoLoading = false
                })
        },
        async getModelInfoFiltered() {
            this.filteredInfoLoading = true
            const { processId } = this
            const params = this.buildVisualizationParams({})

            apiRequest(Api().visualizations.modelInfo({ processId, params }))
                .then((data) => {
                    this.filteredTraces = data?.cases || 0
                    this.infoFiltered = data
                })
                .catch(notifyError)
                .finally(() => {
                    this.filteredInfoLoading = false
                })
        },
        async handleReloadTabData() {
            await this.getModelInfoFiltered()
            this.$refs.activeView?.reloadCurrentView?.()
            this.$refs.activeView?.reloadTabKey?.()

            // Ensure contextual panels refresh their internal lists/models after filters change.
            this.$refs.contextPanel?.initialize?.()
            this.$refs.contextPanel?.initializeVariants?.()
            this.$refs.contextPanel?.initializeLoops?.()
        },

        handleOpenRenameVariantModal(variantId) {
            this.variantRenamedId = variantId
            this.$refs.renameVariantModal?.open?.()
        },
        handleOpenRenameLoopModal(loopId) {
            this.loopRenamedId = loopId
            this.$refs.renameLoopModal?.open?.()
        },
        handleRenameVariant({ newName }) {
            const panel = this.$refs.contextPanel
            if (panel?.renameVariant && this.variantRenamedId !== undefined) {
                panel.renameVariant({ newName, variantRenamedId: this.variantRenamedId })
            }
            this.$refs.renameVariantModal?.close?.()
        },
        handleRenameLoop({ newName }) {
            const panel = this.$refs.contextPanel
            if (panel?.renameLoop && this.loopRenamedId !== undefined) {
                panel.renameLoop({ newName, loopRenamedId: this.loopRenamedId })
            }
            this.$refs.renameLoopModal?.close?.()
        },

        /// /////////////////////////////////////////
        /// /////////////// FILTERS /////////////////
        /// /////////////////////////////////////////

        async handleApplyFilters() {
            const sanitizedFilters = (this.visualizationFilters || []).map((f) => ({ ...f, pending: false }))
            const filteredFilters = sanitizedFilters.filter((f) => !f.toDelete)
            this.setVisualizationFiltersForProcess(this.processId, filteredFilters)
            await this.getCategoricalAttributes()
            this.handleReloadTabData()
        },
        handleOpenFiltersModal() {
            this.openFilterModal = true
        },
        handleCloseFiltersModal() {
            this.openFilterModal = false
        },
        handleOpenFilterSetsModal() {
            this.openFilterSetsModal = true
        },
        handleCloseFiltersetSetsModal() {
            this.openFilterSetsModal = false
        },
        handleOpenCreateFilterSetsModal() {
            this.openCreateFilterSetsModal = true
        },
        handleCloseCreateFilterSetsModal() {
            this.openCreateFilterSetsModal = false
        },
        listenAddFilter() {
            if (this.addFilterListener) {
                this.$eventBus.off('addFilter', this.addFilterListener)
            }

            this.addFilterListener = (filter) => {
                this.handleAddFilter(filter)
            }

            this.$eventBus.on('addFilter', this.addFilterListener)
        },
        handleAddFilter(selected) {
            const filtersToAdd = canonicalizeFiltersToAdd(selected)
            if (filtersToAdd.length) {
                this.appendVisualizationFiltersForProcess(this.processId, filtersToAdd)
            }

            this.handleApplyFilters()
        },
        handleDeleteFilter(filter) {
            this.updateFilters(filter)
            this.handleApplyFilters()
            this.$eventBus.emit('checkForVariantFilter')
        },
        handleResetFilters() {
            this.setVisualizationFiltersForProcess(this.processId, [])
            this.handleApplyFilters()
        },
        handleAddToActiveFilters(filtersSets) {
            this.handleSetFilters(filtersSets, 'add')
        },
        handleReplaceActiveFilters(filterSets) {
            this.handleSetFilters(filterSets, 'replace')
        },
        handleRemoveFilterSets(filterSets) {
            const updatedFilters = this.visualizationFilters.filter((existingFilter) => {
                if (existingFilter.id !== 'FILTER_SET') {
                    return true
                }
                return !filterSets.filters.includes(existingFilter.uuid)
            })

            this.setVisualizationFiltersForProcess(this.processId, updatedFilters)
            this.handleApplyFilters()
        },
        handleSetFilters(selectedSets, mode) {
            const updatedFilters = [...(this.visualizationFilters || [])]
            if (mode === 'replace') {
                selectedSets.forEach((filterSet) => {
                    const formattedFilters = canonicalizeFiltersFromFilterSet(filterSet.filters, filterSet.uuid)
                    formattedFilters.forEach((filter) => {
                        const newFilter = filter
                        const exists = this.checkIfExistFilter(newFilter, updatedFilters)
                        if (!exists) {
                            updatedFilters.push(newFilter)
                        }
                    })
                })
            } else {
                selectedSets.forEach((filterSet) => {
                    const newFilterSet = canonicalizeFilterSetAsFilter(filterSet)
                    const exists = this.checkIfExistFilterSet(newFilterSet, updatedFilters)
                    if (!exists) {
                        updatedFilters.push(newFilterSet)
                    }
                })
            }
            this.setVisualizationFiltersForProcess(this.processId, updatedFilters)
            this.handleApplyFilters()
        },
        formatValue(value) {
            if (!value) return ''
            return typeof value === 'string' ? value : (value || []).join(', ')
        },
        checkIfExistFilter(filter, filtersList = this.visualizationFilters) {
            const normalizeMeta = (f) => {
                const meta = f.meta || {}
                return {
                    attribute: meta.attribute || f.attribute,
                    activity: meta.activity || f.activity,
                    arc: meta.arc || f.arc,
                }
            }

            const filterMeta = normalizeMeta(filter)
            return (filtersList || []).some((existingFilter) => {
                if (existingFilter.id === 'FILTER_SET') return false
                const existingMeta = normalizeMeta(existingFilter)
                return existingFilter.id === filter.id
                    && JSON.stringify(existingFilter.values) === JSON.stringify(filter.values)
                    && JSON.stringify(existingMeta) === JSON.stringify(filterMeta)
            })
        },
        checkIfExistFilterSet(filterSet, filtersList = this.visualizationFilters) {
            return (filtersList || []).some((existingFilter) => existingFilter.id === 'FILTER_SET'
                && existingFilter.uuid === filterSet.uuid)
        },

        handleDrawerChangePercentage(value) {
            this.drawerDisplay.percentageMode = value
            this.callActiveView('setPercentageModeValue', value)
        },
        handleDrawerChangeDirection(value) {
            this.drawerDisplay.directionValue = value
            this.callActiveView('setDirectionValue', value)
        },
        handleDrawerChangeStats(value) {
            this.drawerDisplay.statsValue = value
            this.callActiveView('setStatsValue', value)
        },
        handleDrawerChangeHeatmap(value) {
            this.drawerDisplay.heatmapMode = value
            this.callActiveView('setHeatmapModeValue', value)
        },
        handleDrawerSaveBpmn() {
            this.callActiveView('openSaveBpmn')
        },
        handleDrawerIncreaseFrequencyIndicator() {
            this.callActiveView('handleIncreaseFrequencyIndicator')
        },
        handleDrawerDecreaseFrequencyIndicator() {
            this.callActiveView('handleDecreaseFrequencyIndicator')
        },
    },
}
</script>
<style scoped lang="scss">

.ProcessLayout {
    background-color: $gray-bg;
}

:deep(.ContextDrawer .q-tab-panels),
:deep(.ContextDrawer .q-tab-panel) {
    background: transparent;
}

:deep(.ContextDrawerTabs .q-tab__label) {
    font-weight: 500;
    letter-spacing: 1.25px;
}

:deep(.ContextDrawerTabs .q-tab__content) {
    min-height: $header-height;
}

.Header {
    background: $white;
    height: $header-height;
}

.Footer {
    background: transparent;
    box-sizing: border-box;
    height: auto;
    min-height: $footer-height;
    padding-left: 25px;
    padding-right: 25px;
}

.Screen {
    padding: 25px;
    width: 100%;

    background: $gray-bg;
}

.HeaderRight {
    display: flex;
    align-items: center;
}
.ButtonContainer {
    display: flex;
    align-items: center;
}
.ButtonContainer .ml-1 {
    margin-left: 5px;
}

.ListContainer {
    display: flex;
    flex-direction: column;
}
.TopItems {
    flex: 1;
    display: flex;
    flex-direction: column;
}
.BottomItem {
    margin-top: auto;
}
.ExpandSidebarItem {
    min-height: 36px !important;
    height: 36px;
}
.TopItemSection {
    font-size: 14px;
    font-weight: bold;
    color: #616161;
}
.ItemSection {
    font-size: 15px;
    margin-left: -15px;
}

      .FilterSetsModal {
          position: fixed;
          top: $header-height;
          bottom: $footer-height;
          left: 0;
          right: 0;
          overflow-x: hidden;
          overflow-y: auto;
          outline: 0;
          width: 100%;
          &:deep(.Dialog) {
              min-width: 70%;
              display: flex;
              flex-direction: column;
              max-width: 60%;
              overflow: hidden;
              height: 100%;
              .Body {
                  overflow:auto;
              }
          }
      }
      .UserParent {
        width:100%;
        height:100%;

      }
</style>
